/**
 * Enhanced AdminUserEditPage using the new component system
 * Demonstrates user editing with enhanced form layout and validation
 */

import React, { useEffect, useState } from 'react';
import { FiCalendar, FiMail, FiUser } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

// New component system imports
import {
  Checkbox,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Card, FormField, FormPageLayout, Input } from '@grcs/components';

// Existing hooks and types
import { useUserOperations } from '@grcs/hook/useDatabase';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { formatDoB, validateDob } from '@grcs/utils/validation/validateDob';
import { validateEmail } from '@grcs/utils/validation/validateEmail';
import { hashPassword, parseDOB } from '@grcs/utils/auth/password';
import { StaffPasswordManagement } from './StaffPasswordManagement';

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  dob: string;
  role: UserRole;
  phone?: string;
}

export const AdminUserEditPageEnhanced: React.FC = () => {
  const userOps = useUserOperations();
  const { userId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Optimized state management
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    dob: '',
    role: UserRole.STUDENT,
    phone: '',
  });
  const [errors, setErrors] = useState<Partial<UserFormData>>({});
  const [passwordReset, setPasswordReset] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Optimized data fetching
  useEffect(() => {
    let isCancelled = false;

    const fetchUser = async () => {
      if (!userId) {
        setError('User ID is required');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        console.log('🔍 Fetching user data...', { userId });
        const startTime = Date.now();

        const { data, error: userError } = await userOps.getUserByUserToken(
          userId,
        );

        const loadTime = Date.now() - startTime;
        console.log(`✅ User data loaded in ${loadTime}ms`);

        if (isCancelled) return;

        if (userError) {
          console.error('Error fetching user:', userError);
          setError('Failed to load user data');
          return;
        }

        if (!data || !Array.isArray(data) || data.length === 0) {
          setError('User not found');
          return;
        }

        const userData = data[0] as UserProfile;
        setUser(userData);

        console.log('📅 Raw user data dob:', userData.dob);

        // Format date for HTML date input (YYYY-MM-DD)
        const formatDateForInput = (dateStr: string): string => {
          if (!dateStr) return '';

          try {
            // Handle various date formats
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) {
              console.warn('Invalid date format:', dateStr);
              return '';
            }

            // Format as YYYY-MM-DD for HTML date input
            const formatted = date.toISOString().split('T')[0];
            console.log('📅 Formatted dob for input:', formatted);
            return formatted;
          } catch (error) {
            console.error(
              'Error formatting date:',
              error,
              'Original:',
              dateStr,
            );
            return '';
          }
        };

        // Populate form immediately when data loads
        setFormData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          username: userData.username || '',
          dob: formatDateForInput(userData.dob),
          role: userData.role || UserRole.STUDENT,
          phone: userData.phone || '',
        });
      } catch (err) {
        if (isCancelled) return;
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred');
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchUser();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [userId, userOps]);

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {};

    // Required fields
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailError = validateEmail(formData.email);
      if (emailError) {
        newErrors.email = emailError;
      }
    }

    if (formData.role === UserRole.STUDENT && !formData.username?.trim()) {
      newErrors.username = 'Username is required for students';
    }

    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    } else if (!validateDob(formData.dob)) {
      newErrors.dob = 'Please enter a valid date of birth';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (
      (user?.role === UserRole.STUDENT) !==
      (formData.role === UserRole.STUDENT)
    ) {
      toast({
        title: 'Account conversion required',
        description:
          'Moving between student and staff accounts requires authentication setup. Keep the current account type for now.',
        status: 'error',
      });
      return;
    }
    setIsSubmitting(true);

    try {
      const { phone: _phone, ...formDataWithoutPhone } = formData;
      let studentPassword: string | undefined;
      if (passwordReset && user!.role === UserRole.STUDENT && !user!.auth_uid) {
        studentPassword = await hashPassword(parseDOB(formData.dob));
        if (!studentPassword)
          throw new Error('The student password could not be reset.');
      }

      // Profile saves must never copy a stale staff password or authentication UUID.
      const updatedUser = {
        ...formDataWithoutPhone,
        ...(studentPassword ? { password: studentPassword } : {}),
        dob: formatDoB(formData.dob),
        full_name: `${formData.first_name} ${formData.last_name}`,
      };

      const { error } = await userOps.updateUserById(userId!, updatedUser);

      if (error) {
        toast({
          title: 'Error Updating User',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'User Updated Successfully',
          description: `${formData.first_name} ${formData.last_name} has been updated`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        handleCancel();
      }
    } catch (err: any) {
      console.error('Error updating user:', err);
      toast({
        title: 'Unexpected Error',
        description: 'An unexpected error occurred while updating the user',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (user?.role === UserRole.TEACHER) {
      navigate('/admin/teacher');
    } else if (user?.role === UserRole.STUDENT) {
      navigate('/admin/student');
    } else {
      navigate('/admin');
    }
  };

  const getBreadcrumbItems = () => {
    const items: Array<{ label: string; href?: string; isCurrent?: boolean }> =
      [{ label: 'Admin', href: '/admin' }];

    if (user?.role === UserRole.TEACHER) {
      items.push({ label: 'Teachers', href: '/admin/teacher' });
    } else if (user?.role === UserRole.STUDENT) {
      items.push({ label: 'Students', href: '/admin/student' });
    }

    items.push({ label: 'Edit User', isCurrent: true });
    return items;
  };

  // Handle loading state
  if (isLoading) {
    return (
      <FormPageLayout
        title="Edit User"
        subtitle="Loading user information..."
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Edit User', isCurrent: true },
          ],
        }}
        onSubmit={() => {}}
        onCancel={handleCancel}
        submitText="Update User"
        maxWidth="1200px"
        loading={true}
      >
        <VStack spacing={8} align="stretch">
          <Card padding={6}>
            <VStack spacing={4} align="stretch">
              <Skeleton height="20px" />
              <SkeletonText mt="4" noOfLines={4} spacing="4" />
            </VStack>
          </Card>
        </VStack>
      </FormPageLayout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <FormPageLayout
        title="Edit User"
        subtitle="Error loading user information"
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Edit User', isCurrent: true },
          ],
        }}
        onSubmit={() => {}}
        onCancel={handleCancel}
        submitText="Update User"
        maxWidth="1200px"
        loading={false}
      >
        <VStack spacing={8} align="stretch">
          <Card padding={6}>
            <Text color="red.500" textAlign="center">
              {error}
            </Text>
            <Text color="gray.500" textAlign="center" fontSize="sm">
              Please try refreshing the page or contact support if the problem
              persists.
            </Text>
          </Card>
        </VStack>
      </FormPageLayout>
    );
  }

  // Handle user not found
  if (!user) {
    return (
      <FormPageLayout
        title="Edit User"
        subtitle="User not found"
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Edit User', isCurrent: true },
          ],
        }}
        onSubmit={() => {}}
        onCancel={handleCancel}
        submitText="Update User"
        maxWidth="1200px"
        loading={false}
      >
        <VStack spacing={8} align="stretch">
          <Card padding={6}>
            <Text color="red.500" textAlign="center">
              User not found
            </Text>
            <Text color="gray.500" textAlign="center" fontSize="sm">
              The requested user could not be found. Please check the URL and
              try again.
            </Text>
          </Card>
        </VStack>
      </FormPageLayout>
    );
  }

  return (
    <FormPageLayout
      title={`Update information for ${user.first_name} ${user.last_name}`}
      breadcrumb={{ items: getBreadcrumbItems() }}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      submitText="Update User"
      maxWidth="1200px"
      loading={isSubmitting}
      submitDisabled={isSubmitting}
    >
      <VStack spacing={5} align="stretch">
        <VStack
          as="section"
          spacing={3}
          align="stretch"
          aria-labelledby="personal-information-heading"
        >
          <Text
            as="h2"
            id="personal-information-heading"
            fontSize="md"
            fontWeight="semibold"
          >
            Personal Information
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={5} rowGap={4}>
            <FormField label="First Name" required error={errors.first_name}>
              <Input
                value={formData.first_name}
                onChange={(e) =>
                  handleInputChange('first_name', e.target.value)
                }
                placeholder="Enter first name"
                leftIcon={<FiUser />}
              />
            </FormField>
            <FormField label="Last Name" required error={errors.last_name}>
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter last name"
                leftIcon={<FiUser />}
              />
            </FormField>
            <FormField
              label="Email Address"
              required
              error={errors.email}
              helperText={
                user.role === UserRole.STUDENT
                  ? 'Used for communication'
                  : 'Contact email only; staff login email stays the same.'
              }
            >
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
                leftIcon={<FiMail />}
              />
            </FormField>
            <FormField
              label="Phone Number"
              error={errors.phone}
              helperText="Optional contact number"
            >
              <Input
                value={formData.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter phone number"
              />
            </FormField>
            {formData.role === UserRole.STUDENT && (
              <FormField
                label="Username"
                required
                error={errors.username}
                helperText="Used for student login"
              >
                <Input
                  value={formData.username}
                  onChange={(e) =>
                    handleInputChange('username', e.target.value)
                  }
                  placeholder="Enter username"
                  leftIcon={<FiUser />}
                />
              </FormField>
            )}
            <FormField label="Date of Birth" required error={errors.dob}>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
                leftIcon={<FiCalendar />}
                placeholder="YYYY-MM-DD"
              />
            </FormField>
          </SimpleGrid>
          {user.role === UserRole.STUDENT && !user.auth_uid && (
            <Checkbox
              isChecked={passwordReset}
              onChange={(e) => setPasswordReset(e.target.checked)}
            >
              Reset password to date of birth when saving
            </Checkbox>
          )}
        </VStack>

        {(user.role === UserRole.TEACHER || user.role === UserRole.ADMIN) && (
          <VStack
            as="section"
            spacing={3}
            align="stretch"
            aria-labelledby="password-management-heading"
          >
            <Text
              as="h2"
              id="password-management-heading"
              fontSize="md"
              fontWeight="semibold"
            >
              Password Management
            </Text>
            <StaffPasswordManagement
              userToken={user.user_token}
              authUid={user.auth_uid}
            />
          </VStack>
        )}
      </VStack>
    </FormPageLayout>
  );
};
